/**
 * How a hall pass's MEASURED duration is written (ENG-9644 follow-up).
 *
 * A pass that lasted under a minute used to render as "0 min". On a row the
 * reader already knows describes a pass that happened, "0" reads as either "no
 * time at all" or, more often, "this number is broken" — so a real, short pass
 * looked like a data error. These helpers write it as "<1 min" instead.
 *
 * ## What belongs here, and what deliberately does not
 *
 * These are for a MEASURED ELAPSED duration of ONE pass (or one pending
 * request's wait). Two categories are explicitly NOT in scope, and routing them
 * through here would ship a falsehood:
 *
 *  1. **Configured limits.** A pass type's max duration, the limit shown beside
 *     a live counter, a kiosk's `auto_close_minutes`, a Frequent Flyers filter
 *     threshold ("Total time > 0"). Zero there means "the administrator set
 *     zero", not "a stopwatch read under sixty seconds". Those sites keep their
 *     own plain `${n} min` rendering.
 *
 *  2. **Aggregates** (totals and averages over many passes). See
 *     `formatDuration` in `frequentFlyers.shared.tsx` and `formatMinutes` in
 *     `trends.shared.tsx`, which stay numeric on purpose. The Frequent Flyers
 *     summary total in particular is a sum of ALREADY-ROUNDED per-student
 *     minutes (`LiveFrequentFlyers.tsx`), so a displayed 0 there does not imply
 *     "under a minute of activity" — twenty students out for twenty seconds
 *     each sums to 0 while representing nearly seven real minutes. "<1 min"
 *     would be a false claim about that number.
 *
 * ## Rounding
 *
 * Nothing here rounds. Every caller has already converted seconds to whole
 * minutes with `Math.round(seconds / 60)` at the point it built its row, and
 * this module is deliberately not in the business of changing that arithmetic —
 * doing so would silently move every duration in the app, not just the zeroes.
 *
 * The consequence is worth stating plainly, because it is visible: with
 * round-half-up, a pass only reaches zero minutes below THIRTY seconds. A
 * 45-second pass has already become `1` upstream and renders "1m", not
 * "<1 min". So "<1 min" is precise (a pass showing it really was under a
 * minute) but not exhaustive (some sub-minute passes still show "1m").
 * Making it exhaustive means flooring instead of rounding at each adapter,
 * which also turns a 90-second pass from "2m" into "1m" — a product decision
 * about every duration in the app, not a formatting fix, and so left alone.
 */

/** What a measured duration under one minute is written as, app-wide. */
export const SUB_MINUTE_LABEL = '<1 min';

/**
 * Is this measured duration one we should write as "<1 min" rather than a number?
 *
 * Non-positive covers both the zero case and a negative span, which a clock
 * skew between the pass's start and end stamps can produce. "-3m" is never a
 * useful thing to show a teacher, and a negative span is by definition not a
 * duration of a minute or more.
 *
 * @param minutes Measured whole minutes, or nullish when nothing was recorded.
 * @returns `true` when the "<1 min" label should stand in for the number.
 */
export function isSubMinuteDuration(minutes: number | null | undefined): boolean {
  return minutes != null && Number.isFinite(minutes) && minutes <= 0;
}

/**
 * A single pass's measured minutes in the COMPACT form, for dense tables and
 * cards where the column is already headed "Duration".
 *
 * @param minutes Measured whole minutes for one pass.
 * @returns `"<1 min"` for a sub-minute pass, otherwise `"12m"`.
 */
export function formatPassDurationShort(minutes: number): string {
  return isSubMinuteDuration(minutes) ? SUB_MINUTE_LABEL : `${minutes}m`;
}

/**
 * A single pass's measured minutes in the SPELLED form, for roomier rows and
 * for prose (the printable report's sentence).
 *
 * @param minutes Measured whole minutes for one pass.
 * @returns `"<1 min"` for a sub-minute pass, otherwise `"12 min"`.
 */
export function formatPassDurationLong(minutes: number): string {
  return isSubMinuteDuration(minutes) ? SUB_MINUTE_LABEL : `${minutes} min`;
}
